import { useAuthenticator } from "@aws-amplify/ui-react"
import {
    fetchAuthSession,
    fetchUserAttributes,
    fetchMFAPreference,
} from "aws-amplify/auth"
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react"
import { useNavigate } from "react-router-dom"

import {
    IS_AUTHENTICATED,
    IS_AUTH_CONFIGURING,
} from "../constants/generalConstants"
import {
    DNO_ADMIN,
    ENA_SYS_ADMIN,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
    PUBLIC,
    DISPLAY_ROLES,
    IDENTIFIER_TYPE_DNO_TAG,
    IDENTIFIER_TYPE_LCT_MANU_TAG,
    IDENTIFIER_TYPE_INSTALLER_TAG,
    DNO_APPROVED,
    INSTALLER_APPROVED,
    LCT_MANU_APPROVED,
    DB_ASSESSOR_GEN,
    INSTALLER,
    DB_ASSESSOR_DEMAND,
    AWAITING_SET_UP,
    DB_ASSESSOR_V2G,
} from "../constants/roles"
import useClearSWR from "../hooks/useClearSWR"
import { useAppDispatch } from "../hooks/useDispatch"
import { IUserData } from "../hooks/useGetUserData"
import { setShowAccountSetUp } from "../redux/preferencesSlice"
import { CLOSED } from "../redux/types"
import { SIGN_OUT_SUCCESS } from "../router/routes"
import { arraysAreIdentical } from "../utils/general/generalUtils"

const COGNITO_GROUPS_KW = "cognito:groups"
const CUSTOM_ORG_SET_UP_ATTR = "custom:OrgSetUp"
const PENDING_ATTR_VALUE = "PENDING"
const NO_MFA_GROUPS = [INSTALLER]

const DEFAULT_USER_DATA: IUserData = {
    groups: [PUBLIC],
    displayGroups: [],
    firstName: "",
    lastName: "",
    hasSetMfa: false,
    email: "",
    username: "",
    isAwaitingSetup: false,
    isSetUpPending: false,
    isSignedIn: false,
    isConfiguring: true,
    isMfaRequired: false,
    isDnoApproved: false,
    isDnoAdmin: false,
    isInstallerAdmin: false,
    isInstaller: false,
    isManuAdmin: false,
    isEnaSysAdmin: false,
    isManuApproved: false,
    isGenAssessor: false,
    isDemandAssessor: false,
    isV2gAssessor: false,
    showUnapprovedWarning: false,
    isDnoUnapproved: false,
    isInstallerUnapproved: false,
    isManuUnapproved: false,
    isIDno: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSignOut: () => {},
    isManufacturerForDevice: () => false,
}

export const UserDataContext = createContext<IUserData>(DEFAULT_USER_DATA)

interface UserDataProviderProps {
    children: ReactNode
}

/**
 * Provides user data to the entire app via React Context.
 * Fetches from Cognito once when authStatus changes to
 * "authenticated", and shares the result with all consumers.
 */
export const UserDataProvider = ({
    children,
}: UserDataProviderProps): ReactElement => {
    const { clearCache } = useClearSWR()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { authStatus, signOut } = useAuthenticator((context) => [
        context.authStatus,
    ])

    const isSignedIn = authStatus === IS_AUTHENTICATED

    const [groups, setGroups] = useState<string[]>([PUBLIC])
    const [tokenGroups, setTokenGroups] = useState<string[]>([])
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [username, setUsername] = useState("")
    const [isSetUpPending, setIsSetUpPending] = useState(false)
    const [preferredMfa, setPreferredMfa] = useState<string>("NOMFA")
    const [isDataLoaded, setIsDataLoaded] = useState(false)

    const isConfiguring =
        authStatus === IS_AUTH_CONFIGURING || (isSignedIn && !isDataLoaded)

    const loadUserData = useCallback(async (): Promise<void> => {
        if (!isSignedIn) {
            setGroups([PUBLIC])
            setTokenGroups([])
            setFirstName("")
            setLastName("")
            setEmail("")
            setUsername("")
            setIsSetUpPending(false)
            setPreferredMfa("NOMFA")
            setIsDataLoaded(false)
            return
        }

        setIsDataLoaded(false)

        try {
            const [session, attributes, mfaPref] = await Promise.all([
                fetchAuthSession(),
                fetchUserAttributes(),
                fetchMFAPreference(),
            ])

            const cognitoGroups =
                (session.tokens?.accessToken?.payload?.[
                    COGNITO_GROUPS_KW
                ] as string[]) || []
            setTokenGroups(cognitoGroups)
            setGroups(cognitoGroups.length > 0 ? cognitoGroups : [PUBLIC])

            setFirstName(attributes?.given_name || "")
            setLastName(attributes?.family_name || "")
            setEmail(attributes?.email || "")
            setUsername(attributes?.sub || "")

            const userPending =
                attributes?.[CUSTOM_ORG_SET_UP_ATTR] === PENDING_ATTR_VALUE
            setIsSetUpPending(userPending)

            if (mfaPref?.preferred === "TOTP") {
                setPreferredMfa("SOFTWARE_TOKEN_MFA")
            } else {
                setPreferredMfa("NOMFA")
            }
            setIsDataLoaded(true)
        } catch {
            setGroups([PUBLIC])
            setTokenGroups([])
            setIsDataLoaded(true)
        }
    }, [isSignedIn])

    useEffect(() => {
        loadUserData()
    }, [loadUserData])

    const isMfaRequired =
        preferredMfa === "NOMFA" &&
        !arraysAreIdentical(tokenGroups, NO_MFA_GROUPS)

    const hasSetMfa = preferredMfa === "SOFTWARE_TOKEN_MFA"

    const onSignOut = useCallback(async (): Promise<void> => {
        await clearCache()
        await signOut({ global: true })
        navigate(SIGN_OUT_SUCCESS)
        dispatch(setShowAccountSetUp(CLOSED))
    }, [clearCache, signOut, navigate, dispatch])

    const isEnaSysAdmin = groups.includes(ENA_SYS_ADMIN)
    const isDnoAdmin = groups.includes(DNO_ADMIN)
    const isDnoApproved = groups.includes(DNO_APPROVED)
    const isInstallerAdmin = groups.includes(INSTALLER_ADMIN)
    const isManuAdmin = groups.includes(LCT_MANU_ADMIN)
    const isManuApproved = groups.includes(LCT_MANU_APPROVED)
    const isGenAssessor = groups.includes(DB_ASSESSOR_GEN)
    const isDemandAssessor = groups.includes(DB_ASSESSOR_DEMAND)
    const isV2gAssessor = groups.includes(DB_ASSESSOR_V2G)
    const isAwaitingSetup = groups.includes(AWAITING_SET_UP)
    const isInstaller = groups.includes(INSTALLER)

    const displayGroups = groups.map((group) => {
        let displayGroup = DISPLAY_ROLES[group] ?? group
        if (displayGroup.startsWith(IDENTIFIER_TYPE_DNO_TAG)) {
            displayGroup = displayGroup.replace(IDENTIFIER_TYPE_DNO_TAG, "")
        } else if (displayGroup.startsWith(IDENTIFIER_TYPE_INSTALLER_TAG)) {
            displayGroup = displayGroup.replace(
                IDENTIFIER_TYPE_INSTALLER_TAG,
                ""
            )
        } else if (displayGroup.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG)) {
            displayGroup = displayGroup.replace(
                IDENTIFIER_TYPE_LCT_MANU_TAG,
                ""
            )
        }
        return displayGroup
    })

    const isIDno = groups.some((group) =>
        group.startsWith(`${IDENTIFIER_TYPE_DNO_TAG}I-`)
    )

    const isDnoUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_DNO_TAG)) &&
        !groups.includes(DNO_APPROVED)

    const isInstallerUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_INSTALLER_TAG)) &&
        !groups.includes(INSTALLER_APPROVED)

    const isManuUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_LCT_MANU_TAG)) &&
        !groups.includes(LCT_MANU_APPROVED)

    const showUnapprovedWarning =
        (isDnoUnapproved || isInstallerUnapproved || isManuUnapproved) &&
        !isMfaRequired

    const isManufacturerForDevice = useCallback(
        (deviceRef: string): boolean => {
            const [deviceManufacturer] = deviceRef.split("/")
            const requestorManufacturer =
                groups
                    .find((role) =>
                        role.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG)
                    )
                    ?.split(":")[2] ?? undefined
            return requestorManufacturer === deviceManufacturer
        },
        [groups]
    )

    const value: IUserData = useMemo(
        () => ({
            groups,
            displayGroups,
            firstName,
            lastName,
            hasSetMfa,
            email,
            username,
            isAwaitingSetup,
            isSetUpPending,
            isSignedIn,
            isConfiguring,
            isMfaRequired,
            isDnoAdmin,
            isDnoApproved,
            isInstallerAdmin,
            isInstaller,
            isEnaSysAdmin,
            isManuAdmin,
            isManuApproved,
            isGenAssessor,
            isDemandAssessor,
            isV2gAssessor,
            showUnapprovedWarning,
            isDnoUnapproved,
            isInstallerUnapproved,
            isManuUnapproved,
            isIDno,
            onSignOut,
            isManufacturerForDevice,
        }),
        [
            groups,
            displayGroups,
            firstName,
            lastName,
            hasSetMfa,
            email,
            username,
            isAwaitingSetup,
            isSetUpPending,
            isSignedIn,
            isConfiguring,
            isMfaRequired,
            isDnoAdmin,
            isDnoApproved,
            isInstallerAdmin,
            isInstaller,
            isEnaSysAdmin,
            isManuAdmin,
            isManuApproved,
            isGenAssessor,
            isDemandAssessor,
            isV2gAssessor,
            showUnapprovedWarning,
            isDnoUnapproved,
            isInstallerUnapproved,
            isManuUnapproved,
            isIDno,
            onSignOut,
            isManufacturerForDevice,
        ]
    )

    return (
        <UserDataContext.Provider value={value}>
            {children}
        </UserDataContext.Provider>
    )
}
